import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import './BlogPage.css';

// ============================================
// EDITABLE BLOG CONTENT
// Update this section as needed
// ============================================
const BLOG_CONTENT = {
  // Header
  title: "Welcome to ResumeGlobe's Career Insights & Blog",
  intro: `At ResumeGlobe, we help job seekers across India achieve their dream careers. Our AI-powered toolkit 
    doesn't just create resumes—it enhances them to pass ATS checks and stand out in today's competitive job market.
    <br><br>
    Whether you're a fresher aiming for your first role or a professional looking to climb the ladder, our blog 
    provides actionable tips, career guidance, and industry updates to help you stay ahead.`,
  promoCode: "RG10",
  
  // Main Content
  content: `
    <div class="blog-section-content">
      <h2 class="section-heading">1. Website Updates and Features</h2>
      
      <h3>AI-Powered Resumes and Career Tools</h3>
      <p>Creating a standout resume can be overwhelming, but ResumeGlobe makes it simple:</p>
      
      <div class="content-block">
        <h4>🤖 AI Resume Builder</h4>
        <ul>
          <li>Select your job family and skills</li>
          <li>Choose your experience range</li>
          <li>Generate professional summaries tailored to your career stage</li>
        </ul>
      </div>
      
      <div class="content-block">
        <h4>📊 ATS Score Feedback</h4>
        <ul>
          <li>Check your resume against industry benchmarks</li>
          <li>Get detailed suggestions on improvements</li>
        </ul>
      </div>
      
      <div class="content-block">
        <h4>📥 Resume Downloads & Management</h4>
        <ul>
          <li>Download resumes in multiple formats (.docx, .pdf)</li>
          <li>Our system stacks and organizes your past resumes for easy reference</li>
        </ul>
      </div>
      
      <div class="content-block">
        <h4>✉️ Cover Letter Creation</h4>
        <ul>
          <li>Generate personalized cover letters with a few clicks</li>
        </ul>
      </div>
      
      <div class="content-block">
        <h4>🎯 AI Career Toolkit</h4>
        <ul>
          <li>Explore recommended courses, track skill gaps, and plan your growth</li>
          <li>Includes a career map to guide learning paths</li>
        </ul>
      </div>
      
      <div class="content-block">
        <h4>📝 Take MCQ Test with AI</h4>
        <ul>
          <li>Test your knowledge with AI-generated quizzes tailored to your skills</li>
          <li>Strengthen your profile and identify areas for improvement</li>
        </ul>
      </div>
      
      <p><strong>These tools are designed to help you bridge the gap between your current skills and your career goals.</strong></p>
      
      <h3>Learning Resources & Recommended Courses</h3>
      <p>ResumeGlobe's AI career map provides personalized course recommendations to help you:</p>
      <ul>
        <li>Learn the skills relevant to your target roles</li>
        <li>Prepare for technical assessments with AI-powered MCQ tests</li>
        <li>Track progress and fill skill gaps using the AI career toolkit</li>
      </ul>
      <p>Our curated resources ensure you can focus on the right skills and grow efficiently in your career path.</p>
      
      <h3>Infographics & Product Flow Visuals</h3>
      <p>Here's how your journey with ResumeGlobe works, step by step:</p>
      
      <div class="flow-list">
        <div class="flow-item">
          <strong>1. Check ATS Score</strong>
          <p>Display your resume feedback report with improvement suggestions</p>
        </div>
        
        <div class="flow-item">
          <strong>2. Create Resume</strong>
          <p>Select your job family & skills, click your experience range → generate summary automatically</p>
        </div>
        
        <div class="flow-item">
          <strong>3. Download & Stack Resumes</strong>
          <p>Download in multiple formats; your past resumes are stacked and organized</p>
        </div>
        
        <div class="flow-item">
          <strong>4. Create Cover Letter</strong>
          <p>Generate a professional cover letter in just a few clicks</p>
        </div>
        
        <div class="flow-item">
          <strong>5. Use AI Career Toolkit, MCQ Test & Career Map</strong>
          <p>Identify skill gaps, take AI-powered MCQ tests, and explore your personalized career roadmap</p>
        </div>
      </div>
      
      <p>Visuals make learning easier, engaging, and help users understand how ResumeGlobe's tools work step by step.</p>
    </div>
    
    <div class="blog-section-content">
      <h2 class="section-heading">2. Resume Tips and Preparations</h2>
      
      <h3>Tips to Keep Your ATS Score High</h3>
      <p>Even the best resumes need the right formatting. Keep these tips in mind:</p>
      <ul>
        <li>Keep your resume concise and avoid unnecessary content</li>
        <li>Use standard fonts like Calibri or Arial</li>
        <li>Avoid complex tables or graphics that ATS may misread</li>
        <li>Proofread for grammar and spelling</li>
        <li>Use a clean format (.docx or .pdf)</li>
        <li>Include relevant certifications to boost your profile</li>
      </ul>
      <p>ResumeGlobe's AI tools automatically help you fix these common resume issues.</p>
      
      <h3>Stay Ahead in Your Career & Technical Journey</h3>
      <p>Success today requires adaptability, strategic planning, and continuous learning. With ResumeGlobe's 
      AI-powered tools, expert insights, and personalized learning resources, you can confidently prepare for 
      the future job market.</p>
      <p><strong>Remember:</strong> small, consistent actions—from enhancing your resume to learning the right 
      skills—are what propel your career forward. ResumeGlobe ensures every step counts.</p>
    </div>
    
    <div class="blog-section-content">
      <h2 class="section-heading">3. Tech News around the globe</h2>
      
      <h3>Trending Skills and Career Insights</h3>
      <p>The global job market is evolving rapidly, and AI is transforming the workforce. To stay competitive, 
      continuous learning is essential:</p>
      
      <div class="insight-group">
        <h4>Top Skills in Demand</h4>
        <ul>
          <li>Cloud Computing</li>
          <li>Data Science</li>
          <li>Artificial Intelligence</li>
          <li>Cybersecurity</li>
          <li>DevOps</li>
        </ul>
      </div>
      
      <div class="insight-group">
        <h4>Career Growth Tips</h4>
        <ul>
          <li>Focus on continuous learning</li>
          <li>Develop strong soft skills</li>
          <li>Earn technical certifications</li>
        </ul>
      </div>
      
      <div class="insight-group">
        <h4>AI in the Workforce</h4>
        <ul>
          <li>AI is automating repetitive tasks</li>
          <li>Creates demand for specialized skills</li>
          <li>Reshapes hiring practices and job roles</li>
        </ul>
      </div>
      
      <p>By using our AI career toolkit, you can identify skill gaps, get personalized recommendations, and plan 
      your career effectively.</p>
      
      <h3>Expert Opinions on AI & Careers</h3>
      <div class="expert-quotes">
        <blockquote>
          <p>"AI will dominate the future workforce, reducing repetitive human tasks"</p>
          <cite>— Elon Musk</cite>
        </blockquote>
        
        <blockquote>
          <p>"AI adoption is reshaping industries, creating new opportunities"</p>
          <cite>— Nvidia CEO</cite>
        </blockquote>
        
        <blockquote>
          <p>"Employees must upskill to remain resilient during economic changes"</p>
          <cite>— Infosys CEO</cite>
        </blockquote>
        
        <blockquote>
          <p>"Certain IT and BPO roles may evolve or diminish over the next five years due to AI adoption"</p>
          <cite>— Industry Analysts</cite>
        </blockquote>
      </div>
    </div>
  `
};

const BlogPage = () => {
  const navigate = useNavigate();
  const [blogData, setBlogData] = useState(BLOG_CONTENT);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Real-time blog content from Firestore
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'blogContent', 'main'),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.isPublished !== false) {
            setBlogData({
              title: data.title || BLOG_CONTENT.title,
              intro: data.intro || BLOG_CONTENT.intro,
              promoCode: data.promoCode || BLOG_CONTENT.promoCode,
              content: data.content || BLOG_CONTENT.content
            });
          }
        }
      },
      (err) => {
        // Use default blog content on error
      }
    );
    return () => unsub();
  }, []);

  return (
    <div className="blog-page">
      {/* Blog Header */}
      <div className="blog-header">
        <h1 className="blog-title">{blogData.title}</h1>
        <div className="blog-intro" dangerouslySetInnerHTML={{ __html: blogData.intro }} />
        <div className="promo-banner">
          <span className="promo-icon">💡</span>
          <p className="promo-text">
            <strong>Tip:</strong> Use the promo code <span className="promo-code">{blogData.promoCode}</span> at checkout to avail 
            5% off on any ResumeGlobe service. 
            <a href="/pricing" className="signup-link">Signup Now!</a>
          </p>
        </div>
      </div>

      {/* Mobile/Tablet Top AdSense */}
      <div className="blog-adsense-mobile-top">
        {/* Reserved for Google AdSense - Mobile/Tablet Banner */}
      </div>

      {/* Blog Layout with AdSense Spaces */}
      <div className="blog-layout">
        {/* Left AdSense Space - Desktop Only */}
        <div className="blog-adsense-left">
          {/* Reserved for Google AdSense */}
        </div>

        {/* Main Blog Content */}
        <div className="blog-main-content">
          <div className="blog-content-wrapper">
            <div 
              className="blog-body" 
              dangerouslySetInnerHTML={{ __html: blogData.content }}
            />
          </div>
        </div>

        {/* Right AdSense Space - Desktop Only */}
        <div className="blog-adsense-right">
          {/* Reserved for Google AdSense */}
        </div>
      </div>

      {/* Mobile/Tablet Bottom AdSense */}
      <div className="blog-adsense-mobile-bottom">
        {/* Reserved for Google AdSense - Mobile/Tablet Banner */}
      </div>
    </div>
  );
};

export default BlogPage;
